import { useEffect, useRef } from 'react'

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'details > summary',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/**
 * Reference counts, so two dialogs overlapping during an exit animation
 * cannot leave the page permanently scroll-locked or hidden from screen
 * readers. Only the first open applies the lock; only the last close
 * releases it.
 */
let scrollLockCount = 0
let previousBodyOverflow = ''
let previousBodyPaddingRight = ''

let ariaHiddenCount = 0
let previousRootAriaHidden: string | null = null

function acquireScrollLock() {
  if (scrollLockCount === 0) {
    const { body, documentElement } = document
    previousBodyOverflow = body.style.overflow
    previousBodyPaddingRight = body.style.paddingRight
    const scrollbarWidth = window.innerWidth - documentElement.clientWidth
    body.style.overflow = 'hidden'
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`
    }
  }
  scrollLockCount += 1
}

function releaseScrollLock() {
  scrollLockCount = Math.max(0, scrollLockCount - 1)
  if (scrollLockCount === 0) {
    document.body.style.overflow = previousBodyOverflow
    document.body.style.paddingRight = previousBodyPaddingRight
  }
}

function acquireBackgroundHidden() {
  const root = document.getElementById('root')
  if (!root) return
  if (ariaHiddenCount === 0) {
    previousRootAriaHidden = root.getAttribute('aria-hidden')
    root.setAttribute('aria-hidden', 'true')
  }
  ariaHiddenCount += 1
}

function releaseBackgroundHidden() {
  const root = document.getElementById('root')
  ariaHiddenCount = Math.max(0, ariaHiddenCount - 1)
  if (ariaHiddenCount === 0 && root) {
    if (previousRootAriaHidden === null) {
      root.removeAttribute('aria-hidden')
    } else {
      root.setAttribute('aria-hidden', previousRootAriaHidden)
    }
  }
}

interface UseDialogOptions {
  isOpen: boolean
  onClose: () => void
  /**
   * Selector for the element to focus on open. Falls back to the first
   * focusable descendant, then the panel itself.
   */
  initialFocusSelector?: string
}

/**
 * Everything a dialog owes a keyboard or screen reader user, in one place:
 *
 * - focus moves into the panel on open
 * - Tab and Shift+Tab are trapped inside it
 * - Escape closes it
 * - focus returns to whatever opened it
 * - body scroll is locked while it is open
 * - the rest of the page is hidden from assistive technology
 *
 * Returns a ref for the dialog panel. The panel must be rendered through
 * `DialogPortal`, or hiding the background would hide the dialog too.
 */
export function useDialog({
  isOpen,
  onClose,
  initialFocusSelector,
}: UseDialogOptions) {
  const panelRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLElement | null>(null)

  // Remember what had focus so it can be handed back on close.
  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement as HTMLElement | null
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    acquireScrollLock()
    acquireBackgroundHidden()
    return () => {
      releaseScrollLock()
      releaseBackgroundHidden()
    }
  }, [isOpen])

  // Move focus in, then hand it back to the trigger on close.
  useEffect(() => {
    if (!isOpen) return

    const frame = requestAnimationFrame(() => {
      const panel = panelRef.current
      if (!panel) return
      const target =
        (initialFocusSelector
          ? panel.querySelector<HTMLElement>(initialFocusSelector)
          : null) ??
        panel.querySelector<HTMLElement>(FOCUSABLE) ??
        panel
      target.focus({ preventScroll: true })
    })

    return () => {
      cancelAnimationFrame(frame)
      const trigger = triggerRef.current
      if (trigger && document.contains(trigger)) {
        trigger.focus({ preventScroll: true })
      }
    }
  }, [isOpen, initialFocusSelector])

  // Escape closes. Tab cycles within the panel.
  useEffect(() => {
    if (!isOpen) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
        return
      }

      if (event.key !== 'Tab') return
      const panel = panelRef.current
      if (!panel) return

      const focusable = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter(
        (element) =>
          element.offsetWidth > 0 ||
          element.offsetHeight > 0 ||
          element === document.activeElement,
      )

      if (focusable.length === 0) {
        event.preventDefault()
        panel.focus({ preventScroll: true })
        return
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement

      // Focus outside the panel entirely (first Tab after opening on the
      // panel itself) is pulled back to the correct end.
      if (!panel.contains(active)) {
        event.preventDefault()
        ;(event.shiftKey ? last : first).focus()
        return
      }

      if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      } else if (event.shiftKey && (active === first || active === panel)) {
        event.preventDefault()
        last.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [isOpen, onClose])

  return panelRef
}
